import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { LOGO } from "@/lib/assets";

// Phase Two navigation: six primary groups. Platform, Technology and
// Applications are dropdowns, clicking the label itself opens that group's
// own overview page; the sub-links reach the existing dedicated pages
// without changing any of their URLs. Clinical & Adherence links directly
// (no dropdown) since it is the standalone strategic priority.
const NAV_GROUPS = [
  {
    id: "platform",
    label: "Platform",
    to: "/platform",
    items: [
      { to: "/why", label: "Why WearSpray" },
      { to: "/products", label: "Products" },
      { to: "/technology", label: "How It Works" },
    ],
  },
  {
    id: "technology",
    label: "Technology",
    to: "/intelligence",
    items: [
      { to: "/iq", label: "WearSpray IQ" },
      { to: "/science", label: "The Science" },
      { to: "/patents", label: "Patents" },
    ],
  },
  { id: "clinical", label: "Clinical & Adherence", to: "/clinical" },
  {
    id: "applications",
    label: "Applications",
    to: "/applications",
    items: [
      { to: "/clinical", label: "Medical Aesthetics & Dermatology" },
      { to: "/why", label: "Consumer Skincare" },
      { to: "/global-health", label: "Healthcare & Sanitization" },
      { to: "/products", label: "Personal Protection" },
      { to: "/global-health", label: "Global Health" },
    ],
  },
  {
    id: "about",
    label: "About",
    to: "/about",
    items: [
      { to: "/about", label: "About" },
      { to: "/resume", label: "Founder Story" },
      { to: "/vision", label: "Vision" },
    ],
  },
];

function DesktopGroup({ group }) {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef(null);
  const location = useLocation();

  useEffect(() => {
    return () => {
      if (closeTimer.current) clearTimeout(closeTimer.current);
    };
  }, []);
  const isActive =
    location.pathname === group.to ||
    (group.items || []).some((it) => location.pathname === it.to);

  const show = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpen(true);
  };
  const hide = () => {
    closeTimer.current = setTimeout(() => setOpen(false), 150);
  };

  if (!group.items) {
    return (
      <NavLink
        to={group.to}
        data-testid={`nav-${group.id}-link`}
        className={({ isActive: active }) =>
          `font-body text-sm font-semibold tracking-wide whitespace-nowrap transition-colors duration-300 [text-shadow:0_1px_3px_rgba(0,0,0,0.8)] ${
            active ? "text-white" : "text-white/90 hover:text-accent"
          }`
        }
      >
        {group.label}
      </NavLink>
    );
  }

  return (
    <div
      className="relative"
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
      data-testid={`nav-${group.id}-group`}
    >
      <Link
        to={group.to}
        data-testid={`nav-${group.id}-link`}
        onClick={() => setOpen(false)}
        onFocus={show}
        onBlur={hide}
        className={`flex items-center gap-1.5 font-body text-sm tracking-wide whitespace-nowrap transition-colors duration-300 [text-shadow:0_1px_3px_rgba(0,0,0,0.8)] ${
          isActive ? "text-white" : "text-white/90 hover:text-accent"
        }`}
        aria-expanded={open}
      >
        {group.label}
        <ChevronDown
          size={13}
          className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`}
        />
      </Link>

      {open && (
        <div
          data-testid={`nav-${group.id}-dropdown`}
          className="absolute top-full left-1/2 -translate-x-1/2 pt-4"
        >
          <div className="w-72 backdrop-blur-xl bg-black/90 border border-white/10 rounded-xl py-2.5 shadow-2xl">
            {group.items.map((item, idx) => (
              <Link
                key={`${item.to}-${idx}`}
                to={item.to}
                data-testid={`nav-${group.id}-dropdown-${idx}`}
                onClick={() => setOpen(false)}
                className="block px-6 py-3.5 font-body text-base leading-snug text-zinc-200 hover:text-white hover:bg-white/5 transition-colors"
              >
                {item.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function MobileGroup({ group, onNavigate }) {
  const [expanded, setExpanded] = useState(false);

  if (!group.items) {
    return (
      <NavLink
        to={group.to}
        data-testid={`mobile-nav-${group.id}-link`}
        onClick={onNavigate}
        className={({ isActive }) =>
          `font-body text-base ${isActive ? "text-accent" : "text-zinc-300"}`
        }
      >
        {group.label}
      </NavLink>
    );
  }

  return (
    <div data-testid={`mobile-nav-${group.id}-group`}>
      <div className="flex items-center justify-between">
        <Link
          to={group.to}
          data-testid={`mobile-nav-${group.id}-link`}
          onClick={onNavigate}
          className="font-body text-base text-zinc-300"
        >
          {group.label}
        </Link>
        <button
          type="button"
          data-testid={`mobile-nav-${group.id}-toggle`}
          onClick={() => setExpanded((e) => !e)}
          aria-label={`Toggle ${group.label} submenu`}
          className="p-2 -m-2 text-zinc-500"
        >
          <ChevronDown
            size={16}
            className={`transition-transform duration-300 ${expanded ? "rotate-180" : ""}`}
          />
        </button>
      </div>
      {expanded && (
        <div className="mt-3 ml-4 flex flex-col gap-4 border-l border-zinc-800 pl-4">
          {group.items.map((item, idx) => (
            <Link
              key={`${item.to}-${idx}`}
              to={item.to}
              data-testid={`mobile-nav-${group.id}-dropdown-${idx}`}
              onClick={onNavigate}
              className="font-body text-base text-zinc-300 hover:text-white transition-colors"
            >
              {item.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-black/60 border-b border-white/10"
          : "bg-gradient-to-b from-black/55 via-black/25 to-transparent border-b border-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <Link
          to="/"
          data-testid="brand-logo"
          className="font-heading text-xl font-semibold tracking-tight flex items-center gap-2.5 group"
        >
          <img
            src={LOGO}
            alt="WearSpray logo"
            className="w-9 h-9 object-contain group-hover:scale-110 transition-transform duration-300"
          />
          <span>Wear<span className="text-accent">Spray</span></span>
        </Link>

        <div className="hidden lg:flex items-center gap-6 lg:gap-7">
          {NAV_GROUPS.map((g) => (
            <DesktopGroup key={g.id} group={g} />
          ))}
          <Link
            to="/contact"
            data-testid="nav-partner-cta"
            className="font-body text-sm font-medium bg-[#00e5ff] text-black px-5 py-2.5 hover:bg-[#00b3cc] transition-colors duration-300"
          >
            Partner With Us
          </Link>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((o) => !o)}
          className="lg:hidden text-white"
          aria-label="Toggle menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </nav>

      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden backdrop-blur-xl bg-black/90 border-b border-white/10 px-6 py-6 flex flex-col gap-5 max-h-[80vh] overflow-y-auto"
        >
          {NAV_GROUPS.map((g) => (
            <MobileGroup key={g.id} group={g} onNavigate={() => setOpen(false)} />
          ))}
          <Link
            to="/contact"
            data-testid="mobile-nav-partner-cta"
            onClick={() => setOpen(false)}
            className="font-body text-sm font-medium bg-[#00e5ff] text-black px-5 py-3 text-center"
          >
            Partner With Us
          </Link>
        </div>
      )}
    </header>
  );
}
